import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/DashboardLayout";
import { DealCard } from "@/components/Cards";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { Link } from "react-router-dom";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

export default function Deals() {
    const [deals, setDeals] = useState([]);
    const [groups, setGroups] = useState([]);
    const [saved, setSaved] = useState([]);
    const [query, setQuery] = useState("");
    const [group, setGroup] = useState("all");
    const [tab, setTab] = useState("all"); // all | saved

    useEffect(() => {
        api.get("/public/deals").then((r) => setDeals(r.data));
        api.get("/public/product-groups").then((r) => setGroups(r.data));
        api.get("/member/saved").then((r) => setSaved(r.data));
    }, []);

    // Category filter matches on primary OR additional groups so a
    // cross-listed deal (e.g. Travel Insurance listed under both
    // Insurance and Travel) is discoverable from either category page.
    // The unfiltered "All deals" view naturally shows each deal once
    // because /api/public/deals returns each row a single time.
    const filtered = deals.filter((d) => {
        if (group !== "all") {
            const groupIds = [d.group_id, ...(d.additional_group_ids || [])];
            if (!groupIds.includes(group)) return false;
        }
        if (query && !`${d.title} ${d.vendor}`.toLowerCase().includes(query.toLowerCase()))
            return false;
        if (tab === "saved" && !saved.find((s) => s.id === d.id)) return false;
        return true;
    });

    return (
        <>
            <PageHeader
                eyebrow="Your catalogue"
                title="Deals crafted for you"
                description="Filter by category, search by vendor, or view only your saved perks."
                testid="member-deals-header"
            />

            <div className="px-6 lg:px-12 py-8 space-y-6">
                <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center">
                    <div className="relative flex-1 max-w-md">
                        <Search
                            size={16}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-navy-500"
                        />
                        <Input
                            data-testid="deals-search"
                            placeholder="Search deals or vendors…"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            className="pl-9 bg-white border-navy-100 focus-visible:ring-gold-500 h-11"
                        />
                    </div>
                    <Select value={group} onValueChange={setGroup}>
                        <SelectTrigger
                            data-testid="deals-group-select"
                            className="md:w-56 bg-white border-navy-100 h-11"
                        >
                            <SelectValue placeholder="All categories" />
                        </SelectTrigger>
                        <SelectContent className="bg-white">
                            <SelectItem value="all">All categories</SelectItem>
                            {groups.map((g) => (
                                <SelectItem key={g.id} value={g.id}>
                                    {g.name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    <div className="inline-flex rounded-full bg-white border border-navy-100 p-1 ml-auto">
                        {[
                            ["all", "All deals"],
                            ["saved", `Saved (${saved.length})`],
                        ].map(([k, l]) => (
                            <button
                                key={k}
                                data-testid={`deals-tab-${k}`}
                                onClick={() => setTab(k)}
                                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                                    tab === k
                                        ? "bg-navy-700 text-white"
                                        : "text-navy-500 hover:text-navy-700"
                                }`}
                            >
                                {l}
                            </button>
                        ))}
                    </div>
                </div>

                {filtered.length === 0 ? (
                    <div className="rounded-2xl bg-white border border-navy-100/70 py-16 text-center">
                        <img
                            src="/panda/panda-wheelchair.png"
                            alt=""
                            className="h-32 mx-auto mb-4 opacity-90"
                        />
                        <div className="font-display text-2xl text-navy-700 mb-1">
                            Nothing matched.
                        </div>
                        <div className="text-navy-500 text-sm">
                            Try clearing filters or searching another vendor.
                        </div>
                    </div>
                ) : (
                    <div
                        data-testid="deals-grid"
                        className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5"
                    >
                        {filtered.map((d) => (
                            <DealCard key={d.id} deal={d} to={`/member/deals/${d.id}`} />
                        ))}
                    </div>
                )}
            </div>
        </>
    );
}
